import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { STATUS_OPTIONS, TYPE_OPTIONS, dollarsToCents, centsToDollarInput, isNA, naToNull } from '../constants';
import { projectsApi } from '../api';
import { MoneyInput } from './MoneyInput';
import { PhoneInput } from './PhoneInput';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const EMPTY = {
  name: '',
  status: 'planning',
  project_type: 'other',
  custom_project_type: '',
  project_manager: '',
  description: '',
  customer_name: '',
  customer_phone: '',
  customer_email: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  start_date: '',
  target_completion_date: '',
  customer_budget: '',
  contract_value: '',
  internal_cost_budget: '',
  internal_notes: '',
};

const Section = ({ title, children, hint }) => (
  <div className="border-t border-gray-800 pt-5 first:border-t-0 first:pt-0">
    <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide mb-1">{title}</h4>
    {hint && <p className="text-xs text-gray-500 mb-3">{hint}</p>}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">{children}</div>
  </div>
);

const Field = ({ label, children, help, full }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="text-sm text-gray-300 mb-1.5 block font-medium">{label}</label>
    {children}
    {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
  </div>
);

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls =
  'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';

export const ProjectFormDialog = ({ open, onOpenChange, mode = 'create', project, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && mode === 'edit' && project) {
      setForm({
        ...EMPTY,
        ...project,
        custom_project_type: project.custom_project_type || '',
        project_manager: project.project_manager || '',
        description: project.description || '',
        internal_notes: project.internal_notes || '',
        customer_name: project.customer_name || '',
        customer_phone: project.customer_phone || '',
        customer_email: project.customer_email || '',
        address_line1: project.address_line1 || '',
        address_line2: project.address_line2 || '',
        city: project.city || '',
        state: project.state || '',
        zip_code: project.zip_code || '',
        start_date: project.start_date || '',
        target_completion_date: project.target_completion_date || '',
        customer_budget: centsToDollarInput(project.customer_budget_cents),
        contract_value: centsToDollarInput(project.contract_value_cents),
        internal_cost_budget: centsToDollarInput(project.internal_cost_budget_cents),
      });
    } else if (open && mode === 'create') {
      setForm(EMPTY);
    }
  }, [open, mode, project]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) {
      toast.error('Project name is required');
      return false;
    }
    if (form.customer_email && !isNA(form.customer_email) && !EMAIL_RE.test(form.customer_email)) {
      toast.error('Please enter a valid customer email');
      return false;
    }
    if (
      form.start_date &&
      form.target_completion_date &&
      form.target_completion_date < form.start_date
    ) {
      toast.error('Target completion date cannot be before start date');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    const payload = {
      name: form.name.trim(),
      status: form.status,
      project_type: form.project_type,
      custom_project_type: form.project_type === 'other' ? form.custom_project_type || null : null,
      project_manager: form.project_manager || null,
      description: form.description || null,
      customer_name: form.customer_name || null,
      customer_phone: form.customer_phone || null,
      customer_email: naToNull(form.customer_email),
      address_line1: form.address_line1 || null,
      address_line2: form.address_line2 || null,
      city: form.city || null,
      state: form.state || null,
      zip_code: form.zip_code || null,
      start_date: form.start_date || null,
      target_completion_date: form.target_completion_date || null,
      customer_budget_cents: dollarsToCents(form.customer_budget),
      contract_value_cents: dollarsToCents(form.contract_value),
      internal_cost_budget_cents: dollarsToCents(form.internal_cost_budget),
      internal_notes: form.internal_notes || null,
    };
    try {
      const res =
        mode === 'edit'
          ? await projectsApi.update(project.id, payload)
          : await projectsApi.create(payload);
      toast.success(mode === 'edit' ? 'Project updated' : 'Project created');
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save project');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="project-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl">
            {mode === 'edit' ? 'Edit Project' : 'New Project'}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            {mode === 'edit'
              ? 'Update this project’s details. Money values are entered in dollars.'
              : 'Create a new BuildBroz project. Only a project name is required to start.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Section 1 — Basics */}
          <Section title="Project Basics">
            <Field label="Project Name *" full>
              <Input
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="e.g., Maple St. Rental Rehab"
                className={inputCls}
                data-testid="pf-name"
              />
            </Field>
            <Field label="Project Type">
              <select
                value={form.project_type}
                onChange={(e) => set('project_type', e.target.value)}
                className={selectCls}
                data-testid="pf-type"
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => set('status', e.target.value)}
                className={selectCls}
                data-testid="pf-status"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
            {form.project_type === 'other' && (
              <Field label="Custom Type Description" full>
                <Input
                  value={form.custom_project_type}
                  onChange={(e) => set('custom_project_type', e.target.value)}
                  placeholder="Describe the project type"
                  className={inputCls}
                  data-testid="pf-custom-type"
                />
              </Field>
            )}
            <Field label="Project Manager">
              <Input
                value={form.project_manager}
                onChange={(e) => set('project_manager', e.target.value)}
                className={inputCls}
                data-testid="pf-manager"
              />
            </Field>
            <Field label="Description" full>
              <Textarea
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                rows={2}
                className={inputCls}
                data-testid="pf-description"
              />
            </Field>
          </Section>

          {/* Section 2 — Customer */}
          <Section title="Customer">
            <Field label="Customer Name">
              <Input
                value={form.customer_name}
                onChange={(e) => set('customer_name', e.target.value)}
                className={inputCls}
                data-testid="pf-customer-name"
              />
            </Field>
            <Field label="Customer Phone">
              <PhoneInput
                value={form.customer_phone}
                onChange={(v) => set('customer_phone', v)}
                className={inputCls}
                data-testid="pf-customer-phone"
              />
            </Field>
            <Field label="Customer Email" full>
              <Input
                value={form.customer_email}
                onChange={(e) => set('customer_email', e.target.value)}
                placeholder="name@example.com"
                className={inputCls}
                data-testid="pf-customer-email"
              />
            </Field>
          </Section>

          {/* Section 3 — Property */}
          <Section title="Property">
            <Field label="Address Line 1" full>
              <Input
                value={form.address_line1}
                onChange={(e) => set('address_line1', e.target.value)}
                className={inputCls}
                data-testid="pf-address1"
              />
            </Field>
            <Field label="Address Line 2" full>
              <Input
                value={form.address_line2}
                onChange={(e) => set('address_line2', e.target.value)}
                className={inputCls}
                data-testid="pf-address2"
              />
            </Field>
            <Field label="City">
              <Input
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                className={inputCls}
                data-testid="pf-city"
              />
            </Field>
            <Field label="State">
              <Input
                value={form.state}
                onChange={(e) => set('state', e.target.value)}
                className={inputCls}
                data-testid="pf-state"
              />
            </Field>
            <Field label="ZIP Code">
              <Input
                value={form.zip_code}
                onChange={(e) => set('zip_code', e.target.value)}
                className={inputCls}
                data-testid="pf-zip"
              />
            </Field>
          </Section>

          {/* Section 4 — Schedule */}
          <Section title="Schedule">
            <Field label="Start Date">
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => set('start_date', e.target.value)}
                className={inputCls}
                data-testid="pf-start-date"
              />
            </Field>
            <Field label="Target Completion Date">
              <Input
                type="date"
                value={form.target_completion_date}
                onChange={(e) => set('target_completion_date', e.target.value)}
                className={inputCls}
                data-testid="pf-target-date"
              />
            </Field>
          </Section>

          {/* Section 5 — Financial Planning */}
          <Section
            title="Financial Planning"
            hint="These three values are tracked separately — they are not the same number."
          >
            <Field label="Customer Budget" help="What the customer or investor plans to spend.">
              <MoneyInput
                value={form.customer_budget}
                onChange={(v) => set('customer_budget', v)}
                placeholder="$0.00"
                className={inputCls}
                data-testid="pf-customer-budget"
              />
            </Field>
            <Field label="Contract Value" help="What BuildBroz is charging for the current project.">
              <MoneyInput
                value={form.contract_value}
                onChange={(v) => set('contract_value', v)}
                placeholder="$0.00"
                className={inputCls}
                data-testid="pf-contract-value"
              />
            </Field>
            <Field
              label="Internal Cost Budget"
              help="What BuildBroz expects the project to cost internally."
              full
            >
              <MoneyInput
                value={form.internal_cost_budget}
                onChange={(v) => set('internal_cost_budget', v)}
                placeholder="$0.00"
                className={inputCls}
                data-testid="pf-internal-budget"
              />
            </Field>
          </Section>

          {/* Section 6 — Internal Notes */}
          <Section title="Internal Notes" hint="Internal only — not shown to customers.">
            <Field label="Notes" full>
              <Textarea
                value={form.internal_notes}
                onChange={(e) => set('internal_notes', e.target.value)}
                rows={3}
                className={inputCls}
                data-testid="pf-internal-notes"
              />
            </Field>
          </Section>
        </div>

        <div className="flex gap-3 justify-end border-t border-gray-800 pt-4 sticky bottom-0 bg-[#111111]">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="border-gray-700 text-gray-300"
            data-testid="pf-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold"
            data-testid="pf-submit"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {mode === 'edit' ? 'Save Changes' : 'Create Project'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
